/* 
  This component requires a DesktopHeader component type like 'LoggedInDesktopHeader' or 'LoggedOutDesktopHeader'.
  This is so on certain pages that are logged-out or logged-in only (like the logged-out front page) can choose to 
  exclude logged-in components and be more light-weight while still getting the mobile header and other header specific components.
  Check out DynamicPageHeader to see an implementation of this component.
*/
import * as React from 'react';
import { useContext, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import Media from 'react-media';

import classNames from 'classnames';
import { compose } from 'recompose';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import user from 'js/lib/user';

import { getMetaHeight, isMetaNavEnabled } from 'bundles/ent-website/components/MetaNavigation';
import SmartScrollWrapper from 'bundles/page-header/components/SmartScrollWrapper';
import MobileHeader from 'bundles/page-header/components/mobile/MobileHeader';
import useSimplifiedNav from 'bundles/page-header/hooks/useSimplifiedNav';
import PageHeaderProvider from 'bundles/page-header/providers/PageHeaderProvider';
import RouterContext from 'bundles/page/contexts/RouterContext';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import type { GetS12nCertificateBannerProps, ProductDiscountPromoBannerProps } from 'bundles/page/types/Program';
import withHasPageLoaded from 'bundles/page/utils/withHasPageLoaded';

import 'css!bundles/page-header/components/__styles__/HeaderRightNavV2';
import 'css!bundles/page-header/components/optimized/__styles__/DynamicPageHeader';

const LoadableAuthenticationOption = createLoadableComponent(
  () => import('bundles/user-account/components/AuthenticationOption')
);

export type PropsFromCaller = {
  hideAuth?: boolean;
  isStandaloneAuth?: boolean;
  hideRightNav?: boolean;
  hideSearch?: boolean;
  isSearchPage?: boolean;
  isFrontPage?: boolean;
  showGDPRBanner?: boolean;
  // Tag for wrapper logo (default is H1).
  logoWrapper?: string;
  showShoppingCart?: boolean;
  // Hide notification center in authenticated account dropdown.
  hideNotificationCenter?: boolean;
  getS12nCertificateBannerProps?: GetS12nCertificateBannerProps;
  productDiscountPromoBannerProps?: ProductDiscountPromoBannerProps;
  courseId?: string;
};

type PageHeaderParentWrapperProps = PropsFromCaller & {
  DesktopHeader: React.ComponentType<PropsToDesktopHeader>;
};

type PropsToComponent = PageHeaderParentWrapperProps & {
  hasPageLoaded?: boolean;
};

type PropsToDesktopHeader = Omit<PropsToComponent, 'DesktopHeader'> & {
  setParentClassName: Dispatch<SetStateAction<string>>;
};

const PageHeaderParentWrapper = (props: PropsToComponent) => {
  const { hideAuth, hasPageLoaded, isSearchPage, DesktopHeader, showShoppingCart = true } = props;
  const [extraClassName, setExtraClassName] = useState('');
  const userAgent = useUserAgent();
  const { location } = useContext(RouterContext);

  const showAuthModal = !!location?.query?.authMode;
  const isLoggedOutUser = !user.isAuthenticatedUser();
  const desktopHeaderProps = {
    ...props,
    showShoppingCart,
    setParentClassName: setExtraClassName,
  };
  const metaNavEnabled = isMetaNavEnabled();
  const isSimplifiedPageHeader = useSimplifiedNav();

  // Load/show auth modal if user is requesting or certain events have occurred.
  // Modal only displays on page if authMode query param is present.
  const shouldLoadAuthModal =
    !hideAuth &&
    (showAuthModal ||
      // Start loading if bulk of page is done loading.
      (hasPageLoaded && isLoggedOutUser));

  // We don't want to show header if a page is being opened in a mobile web view. Mobile apps have their own header.
  if (userAgent?.isCourseraMobileApp) {
    return null;
  }

  return (
    <header
      className={classNames(
        'rc-DynamicPageHeader',
        metaNavEnabled ? 'rc-DynamicPageHeader--MetaNav' : null,
        extraClassName
      )}
      data-e2e="page-header"
    >
      <PageHeaderProvider isSimplifiedPageHeader={isSimplifiedPageHeader} hasUnifiedMetaNav={metaNavEnabled}>
        <Media query={{ maxWidth: '1060px' }} defaultMatches={userAgent?.isMobile}>
          {(matches) => {
            if (matches) {
              return <MobileHeader userAgent={userAgent} isSearchPage={isSearchPage} />;
            } else {
              return (
                <SmartScrollWrapper
                  containerHeight={parseInt(getMetaHeight(), 10)}
                  delta={metaNavEnabled ? 50 : Number.MAX_VALUE}
                  style={{ boxShadow: 'none', maxWidth: '100vw' }}
                >
                  <div data-catchpoint="page-header-controls">
                    <DesktopHeader {...desktopHeaderProps} />
                  </div>
                </SmartScrollWrapper>
              );
            }
          }}
        </Media>
        {shouldLoadAuthModal && <LoadableAuthenticationOption query={location?.query} key="authenticationOption" />}
      </PageHeaderProvider>
    </header>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(withHasPageLoaded)(PageHeaderParentWrapper);
